import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { env } from '@config/env';
import { resolveDomain, domainForResolution } from '@shared/api/domainApi';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useNetworkStore } from '@shared/stores/networkStore';
import { useSettingsStore } from '@shared/stores/settingsStore';
import {
  applyEnvApiKeyIfConfigured,
  canUseApiKeyFromEnv,
  effectiveGuardAuthMode,
} from '@shared/lib/guardAuth';

/**
 * Guard NOC boot: branding + regional API + dual auth (JWT or API key).
 * No member profile, onboarding, or domain-config module loading.
 */
export async function bootstrap(): Promise<void> {
  console.info(`[boot] ${env.appName} v${env.version} (${env.buildMode}) auth=${effectiveGuardAuthMode()}`);

  useNetworkStore.getState().startListening().catch(() => undefined);
  await useAuthStore.getState().hydrate();

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const branding = useBrandingStore.getState();
  if (!branding.branding) {
    branding.setDefaultBranding(hostname || env.defaultDomain || 'localhost');
  }

  const target = domainForResolution(hostname || env.defaultDomain);
  if (target) {
    try {
      await resolveDomain(target);
    } catch (error) {
      console.warn('[boot] Domain resolve failed (non-fatal):', error);
    }
  }

  if (canUseApiKeyFromEnv() || effectiveGuardAuthMode() === 'api_key') {
    const applied = await applyEnvApiKeyIfConfigured();
    if (!applied && !useAuthStore.getState().isApiKeyAuth()) {
      console.info('[boot] API key mode — provision at /provision or set VITE_GUARD_* env');
    }
  }

  if (Capacitor.isNativePlatform()) {
    try {
      await KeepAwake.keepAwake();
    } catch {
      /* optional */
    }
    try {
      await SplashScreen.hide();
    } catch {
      /* noop */
    }
  }

  hideSplashElements();
  useSettingsStore.getState().applyTheme();
}

function hideSplashElements(): void {
  if (typeof document === 'undefined') return;
  const splash = document.getElementById('splash-gate');
  if (!splash) return;
  splash.style.transition = 'opacity 280ms cubic-bezier(0.32, 0.72, 0, 1)';
  splash.style.opacity = '0';
  setTimeout(() => splash.remove(), 320);
}
